import { useQueries, useQuery } from '@tanstack/react-query';
import { http, legacy } from '@/shared/api/client';
import { useAuth } from '@/shared/lib/auth';
import { passbookIdsOf } from '@/shared/lib/passbook-access';
import type { Passbook } from '@/shared/types';

export interface MyPassbook {
  _id: string;
  organizationName?: string;
}

/**
 * Lojas da conta, para a tela de escolha e o seletor de marca.
 *
 * A fonte de verdade é o `id_passbooks` do usuário. O endereço antigo
 * (`passbookUser/user/find`) acha as lojas pelo array `users` do cliente, que
 * nem sempre lista quem foi criado depois: usuário fora desse array recebia
 * lista vazia e a tela ficava sem nada. Ele continua aqui só para conta sem
 * `id_passbooks`, como supervisor antigo.
 */
export function useMyPassbooks(options?: { enabled?: boolean }) {
  const { user } = useAuth();
  const enabled = (options?.enabled ?? true) && Boolean(user);
  const ids = passbookIdsOf(user);

  const details = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['passbook', id],
      queryFn: () =>
        legacy.get<{ passbook: Passbook }>(`/passbook/${id}`, { headers: { 'X-Passbook-Id': id } }),
      select: (data: { passbook: Passbook }) => data.passbook,
      staleTime: 5 * 60_000,
      enabled,
    })),
  });

  const byClient = useQuery({
    queryKey: ['my-passbooks', user?._id],
    queryFn: () =>
      http.get<{ passbooks?: MyPassbook[] }>(`/bff/api/passbookUser/user/find/${user!._id}`),
    enabled: enabled && ids.length === 0,
    staleTime: 5 * 60_000,
  });

  const passbooks: MyPassbook[] =
    ids.length > 0
      ? ids.map((id, index) => ({ _id: id, organizationName: details[index]?.data?.organizationName }))
      : (byClient.data?.passbooks ?? []);

  const isLoading = ids.length > 0 ? details.some((q) => q.isLoading) : byClient.isLoading;

  return { passbooks, isLoading };
}
